import { getHierarchicalIdentifier, unwrapConnection } from "@utils/utils";
import React, { useEffect, useState } from "react";

import { ReportGoalType, ReportTargetType } from "@api/graphql/types-and-hooks";
import { GoalLineRow } from "@pages/ClinicalReportDetails/ReportPage/Components/GoalLineRow";
import { TargetRow } from "@pages/ClinicalReportDetails/ReportPage/Components/TargetRow";

interface GoalRowProps {
  goal: ReportGoalType;
  isSelected: boolean;
  selectedTargetId: string | undefined;
  defaultExpanded?: boolean;
  /** When true, only unapproved targets are shown; approved goals with unapproved targets render as ghost anchors. */
  showUnapprovedOnly?: boolean;
  onGoalClick: (goal: ReportGoalType) => void;
  onTargetClick: (target: ReportTargetType, goal: ReportGoalType) => void;
  isRefreshingProgramming?: boolean;
  /** Term to highlight in the goal name */
  highlightTerm?: string;
  /** 0-based continuous goal index across the plan; drives the "N." / "NA" labels. Omit to hide numbering. */
  goalIndex?: number;
}

export const GoalRow: React.FC<GoalRowProps> = ({
  goal,
  isSelected,
  selectedTargetId,
  defaultExpanded,
  showUnapprovedOnly = false,
  onGoalClick,
  onTargetClick,
  isRefreshingProgramming = false,
  highlightTerm,
  goalIndex,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded ?? true);
  const goalTargets = (unwrapConnection(goal.reporttargetSet) as ReportTargetType[]) || [];
  const targets = goalTargets.filter((t) => !t.excludeFromReport);

  const isGhostGoal = showUnapprovedOnly && !!goal.summaryReviewed && targets.length > 0;

  // Open the row once when the selection moves to a target inside this goal, so
  // a just-created custom target is visible even if the row was collapsed.
  // Adjusting state during render (comparing to the previous selection) instead
  // of in an effect keeps `expanded` the single source of truth, so the collapse
  // toggle still works while a target is selected.
  const [prevSelectedTargetId, setPrevSelectedTargetId] = useState<string | undefined>(undefined);
  if (selectedTargetId !== prevSelectedTargetId) {
    setPrevSelectedTargetId(selectedTargetId);
    if (selectedTargetId && targets.some((t) => t.id === selectedTargetId)) {
      setExpanded(true);
    }
  }

  useEffect(() => {
    if (defaultExpanded !== undefined) {
      setExpanded(defaultExpanded);
    }
  }, [defaultExpanded]);

  const handleToggle = () => setExpanded((prev) => !prev);

  const handleGoalClick = () => {
    if (!expanded) setExpanded(true);
    onGoalClick(goal);
  };

  return (
    <div className="flex flex-col gap-2">
      <GoalLineRow
        goal={goal}
        isSelected={isSelected}
        expanded={expanded}
        onToggle={handleToggle}
        onGoalClick={handleGoalClick}
        highlightTerm={highlightTerm}
        isGhostGoal={isGhostGoal}
        hasTargets={targets.length > 0}
        isRefreshingProgramming={isRefreshingProgramming}
        identifierPrefix={goalIndex !== undefined ? getHierarchicalIdentifier(goalIndex) : undefined}
      />
      {expanded && targets.length > 0 && (
        <div className="flex flex-col gap-2">
          {targets.map((target, targetIndex) => (
            <TargetRow
              key={target.id}
              target={target}
              isSelected={selectedTargetId === target.id}
              onClick={() => onTargetClick(target, goal)}
              highlightTerm={highlightTerm}
              identifierPrefix={goalIndex !== undefined ? getHierarchicalIdentifier(goalIndex, targetIndex) : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
};
